import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { StyleAnalysisPromptEditor } from '../components/StyleAnalysisPromptEditor';
import type { SocialNetwork } from '@agency-smm/shared';
import {
  PageWrapper,
  Header,
  Title,
  MainLayout,
  LeftPanel,
  RightPanel,
  PanelTitle,
  ConfigBar,
  ConfigLabel,
  Select,
  CountInput,
  NetworkCheckboxes,
  NetworkChip,
  SectionsStack,
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionBadge,
  SectionBody,
  Textarea,
  ActionBar,
  ResultsSection,
  ResultsHeader,
  ResultsTitle,
  ProviderBadge,
  PostCard,
  PostMeta,
  MetaTag,
  PostContent,
  DynamicTextsGrid,
  DtItem,
  DtLabel,
  DtValue,
  PromptPreview,
  StatusBar,
  LoadingOverlay,
  ResetLink,
  SaveBar,
} from './modPage/styled';
import {
  NETWORKS,
  SECTION_LABELS,
  type MegaPromptSections,
  type GenerateResponse,
  type PreviewResponse,
} from './modPage/types';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ModPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { workspaces } = useWorkspaces();

  // Config state
  const [workspaceId, setWorkspaceId] = useState('');
  const [selectedNetworks, setSelectedNetworks] = useState<SocialNetwork[]>(['instagram']);
  const [count, setCount] = useState(3);

  // Sections state
  const [sections, setSections] = useState<MegaPromptSections | null>(null);
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [editedSections, setEditedSections] = useState<Partial<MegaPromptSections>>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Preview / results
  const [previewPrompt, setPreviewPrompt] = useState<string | null>(null);
  const [results, setResults] = useState<GenerateResponse | null>(null);

  // Loading / status
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ text: string; type: 'info' | 'error' | 'success' } | null>(null);

  // Auth check
  const role = user?.role;
  const canAccessMod = role === 'mod' || role === 'admin' || role === 'super_admin';
  useEffect(() => {
    if (!canAccessMod) {
      navigate('/dashboard');
    }
  }, [canAccessMod, navigate]);

  // Load sections on mount
  useEffect(() => {
    loadSections();
  }, []);

  // Auto-select first workspace
  useEffect(() => {
    if (workspaces.length > 0 && !workspaceId) {
      setWorkspaceId(workspaces[0].id);
    }
  }, [workspaces, workspaceId]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<{ sections: MegaPromptSections; defaults: Record<string, string> }>(
        '/api/mod/playground/sections'
      );
      setSections(response.sections);
      setDefaults(response.defaults);
      setEditedSections({});
    } catch (err) {
      setStatus({ text: `Failed to load: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentSectionValue = useCallback((key: keyof MegaPromptSections): string => {
    if (editedSections[key] !== undefined) return editedSections[key]!;
    if (sections) return sections[key];
    return '';
  }, [sections, editedSections]);

  const isSectionModified = useCallback((key: keyof MegaPromptSections): boolean => {
    return editedSections[key] !== undefined && editedSections[key] !== sections?.[key];
  }, [sections, editedSections]);

  const hasAnyEdits = Object.keys(editedSections).some(k =>
    editedSections[k as keyof MegaPromptSections] !== sections?.[k as keyof MegaPromptSections]
  );

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSectionEdit = (key: keyof MegaPromptSections, value: string) => {
    setEditedSections(prev => ({ ...prev, [key]: value }));
  };

  const handleResetSection = (key: keyof MegaPromptSections) => {
    const promptKeyMap: Record<keyof MegaPromptSections, string> = {
      systemPrompt: 'mega_system_prompt',
      generationRules: 'mega_generation_rules',
      dynamicTextRules: 'mega_dynamic_text_rules',
      imageRulesStock: 'mega_image_rules_stock',
      imageRulesAi: 'mega_image_rules_ai',
    };
    const defaultValue = defaults[promptKeyMap[key]];
    if (defaultValue !== undefined) {
      setEditedSections(prev => ({ ...prev, [key]: defaultValue }));
    }
  };

  const toggleNetwork = (network: SocialNetwork) => {
    setSelectedNetworks(prev => {
      if (prev.includes(network)) {
        if (prev.length === 1) return prev;
        return prev.filter(n => n !== network);
      }
      return [...prev, network];
    });
  };

  const handlePreview = async () => {
    if (!workspaceId) {
      setStatus({ text: 'Select a workspace first', type: 'error' });
      return;
    }
    setPreviewing(true);
    setStatus(null);
    try {
      const response = await apiClient.post<PreviewResponse>('/api/mod/playground/preview', {
        workspaceId,
        networks: selectedNetworks,
        count,
        sections: hasAnyEdits ? editedSections : undefined,
      });
      setPreviewPrompt(`=== SYSTEM PROMPT ===\n${response.systemPrompt}\n\n=== USER PROMPT ===\n${response.prompt}`);
    } catch (err) {
      setStatus({ text: `Preview failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    if (!workspaceId) {
      setStatus({ text: 'Select a workspace first', type: 'error' });
      return;
    }
    setGenerating(true);
    setStatus({ text: 'Generating... this may take 15-60 seconds', type: 'info' });
    setResults(null);
    setPreviewPrompt(null);
    try {
      const response = await apiClient.post<GenerateResponse>('/api/mod/playground/generate', {
        workspaceId,
        networks: selectedNetworks,
        count,
        sections: hasAnyEdits ? editedSections : undefined,
      });
      setResults(response);
      setStatus({
        text: `Generated ${response.posts.length} posts via ${response.provider}${response.failedIndices.length > 0 ? ` (${response.failedIndices.length} failed)` : ''}`,
        type: response.posts.length > 0 ? 'success' : 'error',
      });
    } catch (err) {
      setStatus({ text: `Generation failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!hasAnyEdits) return;
    setSaving(true);
    setStatus(null);
    try {
      const sectionsToSave: Partial<MegaPromptSections> = {};
      for (const [key, val] of Object.entries(editedSections)) {
        if (val !== sections?.[key as keyof MegaPromptSections]) {
          (sectionsToSave as Record<string, string>)[key] = val as string;
        }
      }
      await apiClient.post('/api/mod/playground/save-sections', { sections: sectionsToSave });
      setStatus({ text: 'Sections saved. All future generations will use these prompts.', type: 'success' });
      await loadSections();
    } catch (err) {
      setStatus({ text: `Save failed: ${err instanceof Error ? err.message : 'Unknown'}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!canAccessMod) return null;

  if (loading) {
    return (
      <PageWrapper>
        <LoadingOverlay>Loading playground...</LoadingOverlay>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header>
        <Title>Prompt Playground</Title>
        <Button $variant="outline" $size="sm" onClick={() => navigate('/dashboard')}>
          Back
        </Button>
      </Header>

      <MainLayout>
        {/* LEFT: Config + Actions + Results */}
        <LeftPanel>
          <ConfigBar>
            <ConfigLabel>Workspace</ConfigLabel>
            <Select value={workspaceId} onChange={e => setWorkspaceId(e.target.value)}>
              <option value="">Select...</option>
              {workspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </Select>

            <ConfigLabel>Networks</ConfigLabel>
            <NetworkCheckboxes>
              {NETWORKS.map(n => (
                <NetworkChip key={n.value} $active={selectedNetworks.includes(n.value)}>
                  <input
                    type="checkbox"
                    checked={selectedNetworks.includes(n.value)}
                    onChange={() => toggleNetwork(n.value)}
                  />
                  {n.label}
                </NetworkChip>
              ))}
            </NetworkCheckboxes>

            <ConfigLabel>Posts</ConfigLabel>
            <CountInput
              type="number"
              min={1}
              max={5}
              value={count}
              onChange={e => setCount(Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
            />
          </ConfigBar>

          <ActionBar>
            <Button
              $variant="outline"
              $size="sm"
              onClick={handlePreview}
              disabled={previewing || !workspaceId}
            >
              {previewing ? 'Building...' : 'Preview Prompt'}
            </Button>
            <Button
              $size="sm"
              onClick={handleGenerate}
              disabled={generating || !workspaceId}
            >
              {generating ? 'Generating...' : 'Generate Test'}
            </Button>
          </ActionBar>

          {status && <StatusBar $type={status.type}>{status.text}</StatusBar>}

          {previewPrompt && (
            <>
              <ResultsTitle style={{ marginBottom: '8px' }}>Assembled Prompt</ResultsTitle>
              <PromptPreview>{previewPrompt}</PromptPreview>
            </>
          )}

          {results && results.posts.length > 0 && (
            <ResultsSection>
              <ResultsHeader>
                <ResultsTitle>Results ({results.posts.length} posts)</ResultsTitle>
                <ProviderBadge>{results.provider}</ProviderBadge>
              </ResultsHeader>

              {results.posts.map(post => (
                <PostCard key={post.index}>
                  <PostMeta>
                    <MetaTag $color="#3B82F6">Post {post.index}</MetaTag>
                    {post.stockImagePrompt && (
                      <MetaTag $color="#10B981">stock: {post.stockImagePrompt}</MetaTag>
                    )}
                    {post.aiImagePrompt && (
                      <MetaTag $color="#8B5CF6">ai image</MetaTag>
                    )}
                  </PostMeta>
                  <PostContent>{post.content}</PostContent>
                  <DynamicTextsGrid>
                    <DtItem>
                      <DtLabel>Headline</DtLabel>
                      <DtValue>{post.headline}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>Tagline</DtLabel>
                      <DtValue>{post.tagline}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>CTA</DtLabel>
                      <DtValue>{post.cta}</DtValue>
                    </DtItem>
                    <DtItem>
                      <DtLabel>Quote</DtLabel>
                      <DtValue>{post.quote}</DtValue>
                    </DtItem>
                  </DynamicTextsGrid>
                  {post.aiImagePrompt && (
                    <DtItem style={{ marginTop: '8px' }}>
                      <DtLabel>AI Image Prompt</DtLabel>
                      <DtValue style={{ fontSize: '12px', color: '#64748B' }}>{post.aiImagePrompt}</DtValue>
                    </DtItem>
                  )}
                </PostCard>
              ))}

              {results.rawResponses && results.rawResponses.length > 0 && (
                <details style={{ marginTop: '8px' }} open>
                  <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#64748B', marginBottom: '8px' }}>
                    Raw LLM response{results.rawResponses.length > 1 ? ` (${results.rawResponses.length} batches)` : ''} — lo que devolvió el modelo antes de parsear/limpiar
                  </summary>
                  <PromptPreview>{results.rawResponses.join('\n\n=== NEXT BATCH ===\n\n')}</PromptPreview>
                </details>
              )}

              <details style={{ marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#64748B', marginBottom: '8px' }}>
                  View assembled prompt used
                </summary>
                <PromptPreview>{`=== SYSTEM PROMPT ===\n${results.systemPrompt}\n\n=== USER PROMPT ===\n${results.assembledPrompt}`}</PromptPreview>
              </details>
            </ResultsSection>
          )}
        </LeftPanel>

        {/* RIGHT: Editable Prompt Sections */}
        <RightPanel>
          <PanelTitle>Prompt Sections</PanelTitle>

          <SectionsStack>
            {(Object.keys(SECTION_LABELS) as Array<keyof MegaPromptSections>).map(key => (
              <SectionCard key={key}>
                <SectionHeader onClick={() => toggleSection(key)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '10px', color: '#94A3B8' }}>
                      {expandedSections.has(key) ? '\u25BC' : '\u25B6'}
                    </span>
                    <SectionTitle>{SECTION_LABELS[key]}</SectionTitle>
                    {isSectionModified(key) && <SectionBadge $modified>edited</SectionBadge>}
                  </div>
                  {expandedSections.has(key) && (
                    <ResetLink
                      onClick={e => { e.stopPropagation(); handleResetSection(key); }}
                      title="Reset to default"
                    >
                      reset
                    </ResetLink>
                  )}
                </SectionHeader>
                <SectionBody $open={expandedSections.has(key)}>
                  <Textarea
                    value={getCurrentSectionValue(key)}
                    onChange={e => handleSectionEdit(key, e.target.value)}
                    rows={6}
                  />
                </SectionBody>
              </SectionCard>
            ))}
          </SectionsStack>

          {hasAnyEdits && (
            <SaveBar>
              <Button
                $variant="secondary"
                $size="sm"
                $fullWidth
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save as Active'}
              </Button>
            </SaveBar>
          )}

          {/* Editor del prompt de análisis de estilo (crear estilo desde imagen).
              Autónomo: usa los endpoints genéricos /api/mod/prompts/:key. */}
          <StyleAnalysisPromptEditor />
        </RightPanel>
      </MainLayout>
    </PageWrapper>
  );
}
